import isEqual from 'lodash/isEqual';

import {SuccessResponse as RetentionQueryResponse} from 'common/report/retention/models/types';
import {
  BirthIntervalUnit,
  isRelativeRollingDateRange,
  RetentionAlignmentType,
  RetentionBookmarkParams,
  RetentionType,
  RetentionUnboundedModeType,
} from 'common/report/retention/util/types';
import {flattenQueryResponse, retentionResponseHasData} from 'common/report/retention/util/request';
import {ChartType} from 'common/report/constants';
import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {BookmarkDateRange, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {SortConfig} from 'common/report/util/sorting/types';
import {getUiRowsDataFromParsed} from 'common/report/retention/util/parsing';
import {RetentionBookmarkType} from 'common/report/insights/charts/retention-curve-chart/types';
import {serializedRelativeDatesToRollingFullLengthDateRange} from 'common/util/date-converters';
import {getDataGroupName} from 'common/report/retention/util/format';
import {RetentionPopover} from 'common/report/retention/visualization/popover';
import {getAddictionUnit, getEventDisplayName, getRetentionBucketName} from 'common/report/retention/util';
import {formatMetricNumber, formatPercent} from 'common/util';

import {BookmarkController} from './index';

// TODO once the behavior and metrics project is over we will remove this
export class RetentionBookmarkController implements BookmarkController {
  type = RetentionBookmarkType.RETENTION;
  bookmark: RetentionBookmarkParams;
  queryResults: RetentionQueryResponse;

  constructor(bookmark: RetentionBookmarkParams, results: RetentionQueryResponse) {
    this.bookmark = bookmark;
    this.queryResults = results;
  }

  public hasResults(): boolean {
    return retentionResponseHasData(this.queryResults);
  }

  public getChartType(): ChartType {
    return this.bookmark.chart_type as unknown as ChartType;
  }

  public getSegments(): Segment[] {
    return this.bookmark.group_by;
  }

  public getLegendSelectedSegments(): null | string[] {
    return this.bookmark.selected_segments;
  }

  public getLegendExpandedSegments(): string[] {
    return this.bookmark.expanded_segments || [];
  }

  public hasMultipleRetentionMetrics(): boolean {
    return false;
  }

  public hasRetentionRateOnly(): boolean {
    return this.bookmark.percentage_view;
  }

  public isPercentageView(): boolean {
    return this.bookmark.percentage_view;
  }

  public isCumulative(): boolean {
    return this.bookmark.cumulative;
  }

  getUnboundedMode(): RetentionUnboundedModeType {
    return this.bookmark.unbounded_retention_mode;
  }

  public getLiftComparison(): LiftComparison {
    return this.bookmark.lift_comparison;
  }

  public getSorting(): SortConfig {
    return this.bookmark.sorting;
  }

  public getBornEvent(): string {
    return this.bookmark.born_event;
  }
  public getEvent(): string {
    return this.bookmark.event;
  }
  getRetentionAlignmentType(): RetentionAlignmentType {
    return this.bookmark.retention_alignment_type;
  }
  public getRetentionType(): RetentionType {
    return this.bookmark.retention_type;
  }
  public getRetentionUnit(): TimeUnit {
    return this.bookmark.retention_unit;
  }
  public getDateUnit(): BirthIntervalUnit {
    return this.bookmark.date_unit;
  }
  public getDateRange(): BookmarkDateRange {
    const dateRange = this.bookmark.date_range;
    if (isRelativeRollingDateRange(dateRange)) {
      return serializedRelativeDatesToRollingFullLengthDateRange({
        relativeFrom: Number(dateRange.from),
        relativeTo: Number(dateRange.to),
      });
    }
    return dateRange;
  }
  public getBucketSizes(): number[] {
    return this.bookmark.bucket_sizes;
  }
  getDataGroupId() {
    return this.bookmark.data_group_id;
  }
  getDataGroupName(plural = false): string {
    return getDataGroupName(this.getDataGroupId(), plural);
  }
  public getRetentionBucketIndex(): number {
    return this.bookmark.retention_bucket;
  }

  public createPopover({
    projectTimezone,
    colIdx,
    rowIdx,
    groupedRowIdx = null,
    absoluteValue,
    absoluteDropOffValue,
    cohortSize,
    completeDate,
    enteredValue,
    enteredDate,
    incomplete,
    isAggregated,
  }: {
    projectTimezone: string;
    colIdx: number;
    rowIdx: number;
    groupedRowIdx?: number;
    absoluteValue: string;
    absoluteDropOffValue: string;
    cohortSize: number;
    completeDate: string | false;
    enteredValue: string;
    enteredDate?: Date;
    incomplete: boolean;
    isAggregated: boolean;
  }): RetentionPopover {
    return new RetentionPopover(
      {
        matchingShowClauseIndex: 0,
        bornEventName: this.getBornEvent() && getEventDisplayName(this.getBornEvent()),
        bucketSizes: this.getBucketSizes(),
        dataGroupName: this.getDataGroupName(false),
        dataGroupNamePlural: this.getDataGroupName(true),
        eventName: getEventDisplayName(this.getEvent()),
        groupBy: this.getSegments(),
        retentionUnit: this.getRetentionUnit(),
        dateUnit: this.getDateUnit(),
        dateRange: this.getDateRange(),
        retentionType: this.getRetentionType(),
        retentionAlignmentType: this.getRetentionAlignmentType(),
        unboundedMode: this.getUnboundedMode(),
        isCumulative: this.isCumulative(),
        chartType: this.getChartType(),
        projectTimezone,
        metricLabel: null,
        bookmark: this.bookmark,
      },
      {
        absoluteValue,
        absoluteDropOffValue,
        cohortSize,
        columnIdx: this.getChartType() === ChartType.RetentionTrend ? this.getRetentionBucketIndex() : colIdx, // bucket index i.e. < 1 Day, Day 1, Day 2, ...
        rowIdx,
        ...(groupedRowIdx !== null && {groupedRowIdx}), // interval index i.e. Aug 15, 2022, Aug 22, 2022, ...
        completeDate,
        enteredValue,
        enteredDate,
        incomplete,
        isAggregated,
        propertyAggregation: null,
      },
    );
  }

  isSegmented() {
    return (this.bookmark.group_by || []).length > 0;
  }

  public getCurveChartData(timezone: string) {
    const {segmentKeyToPath, metadata, data, compareToPastData, comparisonMetadata, liftData} = flattenQueryResponse(
      this.queryResults,
      this.bookmark.group_by,
    );

    const {formattedUiRows, formattedLiftRowsData} = getUiRowsDataFromParsed({
      data,
      incompleteBuckets: metadata.incomplete_buckets,
      bookmarkParams: this.bookmark,
      timezone,
      compareToPastData,
      pastIncompleteBuckets: comparisonMetadata && comparisonMetadata.incomplete_buckets,
      liftData,
    });

    return {chartRows: formattedUiRows, formattedLiftRowsData, segmentKeyToPath};
  }

  public formatRetentionCurveXAxisLabel(): (number: number) => string {
    return this.getRetentionType() === `addiction`
      ? this.frequencyBucketLabel(this.getDateUnit())
      : this.retentionBucketLabel();
  }

  public formatRetentionCurveYAxisLabel(): (number: number) => string {
    if (this.getLiftComparison()) {
      return this.percentLabel;
    }
    return this.isPercentageView() ? this.percentLabel : this.absoluteLabel;
  }

  percentLabel(value: number) {
    return formatPercent(value, 0);
  }

  absoluteLabel(value: number) {
    return formatMetricNumber(value);
  }

  retentionBucketLabel() {
    const retentionType = this.getRetentionType();
    const retentionUnit = this.getRetentionUnit();
    const dateUnit = this.getDateUnit();
    const bucketSizes = this.getBucketSizes();
    return function (value: number) {
      return getRetentionBucketName({
        bucketIndex: value,
        retentionType,
        retentionUnit,
        dateUnit,
        bucketSizes,
      });
    };
  }

  frequencyBucketLabel(unit: string) {
    return function (value: number) {
      return `${value + 2} ${getAddictionUnit(unit)}s`;
    };
  }

  public isEquivalent(bookmark: RetentionBookmarkParams, results: RetentionQueryResponse) {
    return isEqual(this.bookmark, bookmark) && isEqual(this.queryResults, results);
  }
}
